import useEventTopicsStore from '@/store/eventTopics.ts'
import { getFullApiUrl } from '@/config/apiConfig'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { api } from '@/lib/api-client'

// Cache for the Mercure JWT token with expiration tracking
let mercureJwtToken: string | null = null
let tokenExpiry: number | null = null
let tokenFetchPromise: Promise<string | null> | null = null

// Token lifetime from backend is 1 hour (3600s), refresh 5 min before expiry
const TOKEN_LIFETIME_MS = 3600 * 1000
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000

// Store native connections for cleanup and token refresh (prevents memory leak)
type NativeConnection = {
  controller: AbortController
  refreshTimer: ReturnType<typeof setTimeout> | null
}
const nativeConnections = new Map<string, NativeConnection>()

const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[Mercure]', ...args)

/**
 * Check if the cached token is still valid (not expired or about to expire)
 */
function isTokenValid(): boolean {
  if (!mercureJwtToken || !tokenExpiry) return false
  const now = Date.now()
  // Refresh if within buffer period of expiry
  return now + TOKEN_REFRESH_BUFFER_MS < tokenExpiry
}

/**
 * Fetch Mercure subscriber JWT from the backend.
 * This token is used for authentication on native platforms where cookies don't work with EventSource.
 * Automatically refreshes token before expiration.
 */
async function getMercureToken(): Promise<string | null> {
  if (mercureJwtToken && isTokenValid()) {
    return mercureJwtToken
  }

  if (tokenFetchPromise) {
    return tokenFetchPromise
  }

  tokenFetchPromise = (async () => {
    try {
      const response = await api.get<{ token: string }>('/mercure-token')
      mercureJwtToken = response.data.token
      tokenExpiry = Date.now() + TOKEN_LIFETIME_MS
      return mercureJwtToken
    } catch (error) {
      console.warn('[Mercure] Failed to get token, SSE may not work:', error)
      return null
    } finally {
      tokenFetchPromise = null
    }
  })()

  return tokenFetchPromise
}

/**
 * Clear the cached Mercure token (call on logout)
 */
export function clearMercureToken(): void {
  mercureJwtToken = null
  tokenExpiry = null
}

/**
 * Close a native SSE connection by topic (for cleanup)
 */
export function closeNativeEventSource(topic: string): void {
  const conn = nativeConnections.get(topic)
  if (conn) {
    if (conn.refreshTimer) clearTimeout(conn.refreshTimer)
    conn.controller.abort()
    nativeConnections.delete(topic)
  }
}

/**
 * Close all native SSE connections (call on logout)
 */
export function closeAllNativeEventSources(): void {
  nativeConnections.forEach((conn, topic) => {
    if (conn.refreshTimer) clearTimeout(conn.refreshTimer)
    conn.controller.abort()
    useEventTopicsStore.getState().removeTopic(topic)
  })
  nativeConnections.clear()
}


/**
 * Subscribes to a Mercure topic if not already subscribed.
 * Invokes `onMessageCallback` when a new event arrives.
 *
 * On iOS native: Uses fetch-event-source which works with CapacitorHttp,
 * since native EventSource is blocked by WKWebView for cross-origin requests.
 */
export function subscribeToMercureTopic(
  topic: string,
  onMessageCallback: (event: MessageEvent) => void,
): EventSource | null {
  if (import.meta.env.VITE_SKIP_EVENT_SUBSCRIPTIONS === 'true') {
    return null
  }

  const { subscribedTopics, addTopic, removeTopic } = useEventTopicsStore.getState()

  if (subscribedTopics.has(topic)) {
    return null
  }

  // Add topic immediately to prevent race condition when multiple components
  // try to subscribe to the same topic simultaneously
  addTopic(topic)

  // For native platforms (iOS/Android), use fetch-based SSE (works with CapacitorHttp)
  // Native EventSource has cross-origin issues on mobile WebViews
  if (isNativePlatform()) {
    createNativeFetchEventSource(topic, onMessageCallback).catch(error => {
      console.error('[Mercure Native] Failed to create SSE connection:', error)
      removeTopic(topic)
    })
    return null // Return null immediately, subscription is async
  }

  // Web: Use standard EventSource with cookies
  const mercureUrl = getFullApiUrl(`/.well-known/mercure?topic=${topic}`)
  const eventSource = new EventSource(mercureUrl, { withCredentials: true })

  eventSource.onmessage = (event: MessageEvent) => {
    onMessageCallback(event)
  }

  eventSource.onerror = () => {
    if (eventSource.readyState === EventSource.CLOSED) {
      removeTopic(topic)
    }
  }

  return eventSource
}

/**
 * Create SSE connection for native platforms (iOS/Android).
 *
 * Uses native EventSource with JWT in URL parameter instead of fetch-event-source.
 * CapacitorHttp doesn't properly support SSE streaming (it buffers responses),
 * but native EventSource works when authentication is via URL parameter.
 */
async function createNativeFetchEventSource(
  topic: string,
  onMessageCallback: (event: MessageEvent) => void,
): Promise<void> {
  const { removeTopic } = useEventTopicsStore.getState()

  // Try to get JWT token for authentication
  const token = await getMercureToken()
  if (!token) {
    debug('Native: No JWT token available, cannot connect to Mercure')
    removeTopic(topic)
    return
  }

  // Use JWT in URL parameter - native EventSource doesn't support custom headers,
  // and CapacitorHttp's fetch interception doesn't handle SSE streaming properly
  const mercureUrl = getFullApiUrl(
    `/.well-known/mercure?topic=${encodeURIComponent(topic)}&authorization=${encodeURIComponent(token)}`
  )

  debug('Native: Creating EventSource for topic:', topic)

  try {
    // Use native EventSource - it bypasses CapacitorHttp and works with SSE
    const eventSource = new EventSource(mercureUrl)

    const abortController = {
      abort: () => eventSource.close(),
      signal: new AbortController().signal,
    } as AbortController
    nativeConnections.set(topic, { controller: abortController, refreshTimer: null })

    eventSource.onopen = () => {
      debug('Native: SSE connected for topic:', topic)
    }

    eventSource.onmessage = (event: MessageEvent) => {
      debug('Native: SSE message received for topic:', topic)
      onMessageCallback(event)
    }

    eventSource.onerror = (error) => {
      debug('Native: SSE error for topic:', topic, error)
      if (eventSource.readyState === EventSource.CLOSED) {
        debug('Native: SSE connection permanently closed for topic:', topic)
        const c = nativeConnections.get(topic)
        if (c?.refreshTimer) clearTimeout(c.refreshTimer)
        nativeConnections.delete(topic)
        removeTopic(topic)
      }
    }

    const scheduleTokenRefresh = () => {
      const refreshDelay = TOKEN_LIFETIME_MS - TOKEN_REFRESH_BUFFER_MS
      const timer = setTimeout(async () => {
        const conn = nativeConnections.get(topic)
        if (!conn) return

        debug('Native: Token refresh triggered for topic:', topic)

        // Remove from map BEFORE abort so that any queued onerror from the old
        // EventSource sees no entry and becomes a no-op, preventing it from
        // calling removeTopic() while we're still reconnecting.
        nativeConnections.delete(topic)
        conn.controller.abort()

        const freshToken = await getMercureToken()
        if (!freshToken) {
          debug('Native: Failed to refresh token, SSE will not reconnect')
          nativeConnections.delete(topic)
          removeTopic(topic)
          return
        }

        const freshUrl = getFullApiUrl(
          `/.well-known/mercure?topic=${encodeURIComponent(topic)}&authorization=${encodeURIComponent(freshToken)}`,
        )

        const newEventSource = new EventSource(freshUrl)
        const newController = {
          abort: () => newEventSource.close(),
          signal: new AbortController().signal,
        } as AbortController

        newEventSource.onopen = () => {
          debug('Native: SSE reconnected after token refresh for topic:', topic)
        }

        newEventSource.onmessage = (event: MessageEvent) => {
          debug('Native: SSE message received for topic:', topic)
          onMessageCallback(event)
        }

        newEventSource.onerror = (error) => {
          debug('Native: SSE error for topic:', topic, error)
          if (newEventSource.readyState === EventSource.CLOSED) {
            debug('Native: SSE connection permanently closed for topic:', topic)
            const c = nativeConnections.get(topic)
            if (c?.refreshTimer) clearTimeout(c.refreshTimer)
            nativeConnections.delete(topic)
            removeTopic(topic)
          }
        }

        nativeConnections.set(topic, { controller: newController, refreshTimer: null })
        scheduleTokenRefresh()
      }, refreshDelay)

      const conn = nativeConnections.get(topic)
      if (conn) {
        conn.refreshTimer = timer
      }
    }

    scheduleTokenRefresh()
  } catch (error) {
    debug('Native: Failed to create EventSource:', error)
    nativeConnections.delete(topic)
    removeTopic(topic)
  }
}
